// src/pages/TrackOrderPage.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function TrackOrderPage() {
  const [trackingId, setTrackingId] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const API = process.env.REACT_APP_API_BASE_URL; 

  const handleSubmit = async e => {
    e.preventDefault();
    if (!trackingId) return;
    setLoading(true);
    setError('');
    setOrder(null);
    try {
      const res = await fetch(`${API}/orders/track/${trackingId}`);
      if (!res.ok) throw new Error('Order not found');
      const data = await res.json();
      setOrder(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch order');
    }
    setLoading(false);
  };

  const statusSteps = ['pending', 'paid', 'shipped', 'sent'];

  const steps = [
    { name: 'pending', icon: 'bx-time-five', label: 'Placed' },
    { name: 'paid', icon: 'bx-credit-card', label: 'Paid' },
    { name: 'shipped', icon: 'bx-package', label: 'Shipped' },
    { name: 'sent', icon: 'bx-check-double', label: 'Delivered' }
  ];

  return (
    <div className="max-w-3xl mx-auto mt-24 px-4 py-8 space-y-8 font-[Poppins] bg-[#fcfdfc] min-h-[70vh]">
      <div className="text-center space-y-2">
        <h1 className="text-2xl sm:text-3xl font-black text-slate-800 font-[Montserrat]">Track Your Order</h1>
        <p className="text-xs sm:text-sm text-slate-400 font-medium">Enter your tracking ID to view delivery updates</p>
      </div>

      {/* Input Form */}
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row justify-center gap-3 bg-white p-2 rounded-[2rem] border border-slate-100 shadow-sm max-w-lg mx-auto">
        <input
          type="text"
          placeholder="Enter your Tracking ID"
          value={trackingId}
          onChange={e => setTrackingId(e.target.value)}
          className="flex-1 px-5 py-3 rounded-full text-slate-800 outline-none text-sm font-semibold placeholder-slate-400 bg-slate-50/50"
          required
        />
        <button
          type="submit"
          className="bg-green-700 hover:bg-green-800 text-white px-6 py-3 rounded-full font-bold shadow-md hover:shadow-lg transition duration-300 text-sm"
        >
          {loading ? (
            <span className="flex items-center gap-1"><i className="bx bx-loader-alt animate-spin" /> Tracking</span>
          ) : (
            'Track Order'
          )}
        </button>
      </form>

      {/* Error */}
      {error && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-red-600 text-center font-bold text-sm bg-red-50 border border-red-100 p-3 rounded-2xl max-w-md mx-auto"
        >
          <i className="bx bx-error-circle" /> {error}
        </motion.p>
      )}

      {/* Order Details */}
      <AnimatePresence>
        {order && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 15 }}
            className="bg-white border border-slate-100 rounded-3xl shadow-sm p-6 sm:p-8 space-y-6"
          >
            <div className="flex justify-between items-center border-b pb-4">
              <h2 className="text-lg sm:text-xl font-black text-slate-800 font-[Montserrat] flex items-center gap-2">
                <i className="bx bx-shopping-bag text-green-700 text-2xl" /> Order Summary
              </h2>
              <span className="text-xs font-bold bg-green-50 text-green-800 px-3 py-1 rounded-full border border-green-100">
                Active status: <span className="uppercase font-black text-[10px]">{order.status}</span>
              </span>
            </div>

            {/* Customer Details info table */}
            <div className="grid md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-2xl text-xs sm:text-sm font-semibold text-slate-600">
              <div className="space-y-1.5">
                <p><span className="text-slate-400 font-medium">Customer:</span> {order.customer.name}</p>
                <p><span className="text-slate-400 font-medium">Phone:</span> {order.customer.phone}</p>
                <p><span className="text-slate-400 font-medium">Email:</span> {order.customer.email}</p>
              </div>
              <div className="space-y-1.5">
                <p><span className="text-slate-400 font-medium">Address:</span> {order.customer.addressLine1}, {order.customer.addressLine2 ? order.customer.addressLine2 + ', ' : ''}{order.customer.city}, {order.customer.province}, {order.customer.postalCode}</p>
              </div>
            </div>

            {/* Items */}
            <div className="space-y-2">
              <h3 className="font-black text-slate-800 text-sm uppercase tracking-wider font-[Montserrat]">Ordered Items</h3>
              <div className="divide-y divide-slate-100 border border-slate-100 rounded-2xl overflow-hidden bg-white">
                {order.items.map(item => (
                  <div key={item.productId} className="flex justify-between items-center p-3 text-xs sm:text-sm font-semibold text-slate-700">
                    <span>{item.name} <span className="text-slate-400 font-medium">× {item.quantity}</span></span>
                    <span className="font-bold text-slate-800">${(item.amount / 100).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Stock Issues */}
            {order.stockIssues && order.stockIssues.length > 0 && (
              <div className="p-4 bg-red-50 border border-red-100 rounded-2xl space-y-1.5">
                <h4 className="font-black text-red-700 text-xs sm:text-sm flex items-center gap-1.5 font-[Montserrat]">
                  <i className="bx bx-error-circle text-lg" /> Vendor Stock Alerts:
                </h4>
                {order.stockIssues.map(issue => (
                  <p key={issue.productId} className="text-xs text-red-600 font-semibold pl-6">
                    Product requested: {issue.requested}, available: {issue.available} remaining.
                  </p>
                ))}
              </div>
            )}

            {/* Order Status pipeline */}
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <h3 className="font-black text-slate-800 text-sm uppercase tracking-wider font-[Montserrat] mb-6">Delivery Timeline</h3>
              <div className="flex flex-col sm:flex-row justify-between items-center gap-6 sm:gap-0 relative">
                {steps.map((step, idx) => {
                  const done = statusSteps.indexOf(order.status) >= statusSteps.indexOf(step.name);
                  const isCurrent = order.status === step.name;
                  return (
                    <div key={step.name} className="flex-1 w-full sm:w-auto text-center relative flex sm:flex-col items-center justify-between sm:justify-start gap-4 sm:gap-2">
                      {/* Timeline bar line (hidden on mobile, shown on desktop) */}
                      {idx < steps.length - 1 && (
                        <div className="hidden sm:block absolute top-5 left-1/2 w-full h-[3px] bg-slate-100 -z-10">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: done ? '100%' : '0%' }}
                            className="h-full bg-green-700"
                            transition={{ duration: 0.5, delay: idx * 0.1 }}
                          />
                        </div>
                      )}

                      {/* Icon bubble */}
                      <motion.div
                        className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 z-10 shadow-sm
                          ${
                            done 
                              ? 'bg-green-700 border-green-700 text-white shadow-green-700/10' 
                              : 'bg-white border-slate-200 text-slate-400'
                          }
                          ${isCurrent ? 'ring-4 ring-green-700/15 scale-110' : ''}`}
                      >
                        <i className={`bx ${step.icon} text-lg`} />
                      </motion.div>

                      {/* Text label */}
                      <div className="text-right sm:text-center flex-1 sm:flex-initial">
                        <span className={`text-xs font-black block font-[Montserrat] ${done ? 'text-green-800 font-black' : 'text-slate-400'}`}>
                          {step.label}
                        </span>
                        <span className="text-[10px] text-slate-400 font-medium block capitalize">
                          {step.name}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
